/**
 * MentionRepository - Database operations for mentions collection
 */

import { BaseRepository, where } from './BaseRepository';
import { doc } from 'firebase/firestore';
import { recordBehaviourMention } from '../recordBehaviourMention';

export class MentionRepository extends BaseRepository {
  constructor() {
    // CRITICAL: Enable hierarchical paths - mentions are now stored at schools/{schoolId}/mentions/{mentionId}
    super('mentions', { isHierarchical: true });
  }

  async create(data) {
    return recordBehaviourMention(data);
  }

  async createWithId(data, id) {
    return recordBehaviourMention(data, id);
  }

  /**
   * Get mentions for a specific student
   * @param {string} studentId - Student ID
   * @param {Object} options - Query options (MUST include schoolId for hierarchical collections)
   * @returns {Promise<Array>} Array of mentions
   */
  async getByStudentId(studentId, options = {}) {
    if (!options.schoolId) {
      throw new Error('schoolId is required for hierarchical mentions collection');
    }

    const constraints = [
      where('studentId', '==', studentId)
    ];

    // schoolId is no longer added as a where clause - it's part of the path
    // But we pass it to queryDocuments for hierarchical routing

    if (options.actioned !== undefined) {
      constraints.push(where('actioned', '==', options.actioned));
    }

    // Don't use orderBy or date filters - fetch all and sort client-side
    // This avoids needing composite indexes and handles mentions without issuedAt

    const results = await this.queryDocuments(constraints, options.schoolId);

    // Sort client-side by issuedAt (falling back to createdAt)
    results.sort((a, b) => {
      const dateA = a.issuedAt || a.createdAt;
      const dateB = b.issuedAt || b.createdAt;
      const timeA = dateA?.toMillis?.() || new Date(dateA).getTime();
      const timeB = dateB?.toMillis?.() || new Date(dateB).getTime();
      return timeB - timeA; // Desc order
    });

    // Apply date filtering client-side
    let filtered = results;
    if (options.startDate) {
      const startTime = options.startDate.getTime();
      filtered = filtered.filter(m => {
        const date = m.issuedAt || m.createdAt;
        const time = date?.toMillis?.() || new Date(date).getTime();
        return time >= startTime;
      });
    }
    if (options.endDate) {
      const endTime = options.endDate.getTime();
      filtered = filtered.filter(m => {
        const date = m.issuedAt || m.createdAt;
        const time = date?.toMillis?.() || new Date(date).getTime();
        return time <= endTime;
      });
    }

    // Apply limit client-side
    if (options.limit) {
      filtered = filtered.slice(0, options.limit);
    }

    return filtered;
  }

  /**
   * Get mentions for a specific class
   * @param {string} classId - Class ID
   * @param {Object} options - Query options (MUST include schoolId for hierarchical collections)
   * @returns {Promise<Array>} Array of mentions
   */
  async getByClassId(classId, options = {}) {
    if (!options.schoolId) {
      throw new Error('schoolId is required for hierarchical mentions collection');
    }

    const constraints = [
      where('classId', '==', classId)
    ];

    if (options.actioned !== undefined) {
      constraints.push(where('actioned', '==', options.actioned));
    }

    // Fetch all and sort client-side (same as getByStudentId)
    const results = await this.queryDocuments(constraints, options.schoolId);

    // Sort client-side by issuedAt (falling back to createdAt)
    results.sort((a, b) => {
      const dateA = a.issuedAt || a.createdAt;
      const dateB = b.issuedAt || b.createdAt;
      const timeA = dateA?.toMillis?.() || new Date(dateA).getTime();
      const timeB = dateB?.toMillis?.() || new Date(dateB).getTime();
      return timeB - timeA;
    });

    // Apply date filtering client-side
    let filtered = results;
    if (options.startDate) {
      const startTime = options.startDate.getTime();
      filtered = filtered.filter(m => {
        const date = m.issuedAt || m.createdAt;
        const time = date?.toMillis?.() || new Date(date).getTime();
        return time >= startTime;
      });
    }

    if (options.limit) {
      filtered = filtered.slice(0, options.limit);
    }

    return filtered;
  }

  /**
   * Get today's mentions
   * @param {Object} options - Query options (MUST include schoolId for hierarchical collections)
   * @returns {Promise<Array>} Array of today's mentions
   */
  async getTodayMentions(options = {}) {
    if (!options.schoolId) {
      throw new Error('schoolId is required for hierarchical mentions collection');
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const constraints = [];

    if (options.studentId) {
      constraints.push(where('studentId', '==', options.studentId));
    }

    if (options.classId) {
      constraints.push(where('classId', '==', options.classId));
    }

    const results = await this.queryDocuments(constraints, options.schoolId);

    // Filter for today and sort client-side by issuedAt (falling back to createdAt)
    const todayTime = today.getTime();
    const filtered = results.filter(m => {
      const date = m.issuedAt || m.createdAt;
      const time = date?.toMillis?.() || new Date(date).getTime();
      return time >= todayTime;
    });

    filtered.sort((a, b) => {
      const dateA = a.issuedAt || a.createdAt;
      const dateB = b.issuedAt || b.createdAt;
      const timeA = dateA?.toMillis?.() || new Date(dateA).getTime();
      const timeB = dateB?.toMillis?.() || new Date(dateB).getTime();
      return timeB - timeA;
    });

    return filtered;
  }

  /**
   * Get mentions for rule evaluation
   * @param {string} studentId - Student ID
   * @param {Object} scope - Scope configuration (MUST include schoolId)
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @returns {Promise<Array>} Array of mentions
   */
  async getMentionsForRuleEvaluation(studentId, scope, startDate, endDate) {
    if (!scope.schoolId) {
      throw new Error('schoolId is required in scope for hierarchical mentions collection');
    }

    const constraints = [
      where('studentId', '==', studentId),
      where('actioned', '==', false)
    ];

    // Fetch all and filter client-side to handle mentions without issuedAt
    const results = await this.queryDocuments(constraints, scope.schoolId);

    // Filter by date range client-side using issuedAt || createdAt fallback
    const startTime = startDate.getTime();
    const endTime = endDate.getTime();

    const filtered = results.filter(m => {
      const date = m.issuedAt || m.createdAt;
      const time = date?.toMillis?.() || new Date(date).getTime();
      return time >= startTime && time <= endTime;
    });

    // Sort by issuedAt (falling back to createdAt) ascending for rule evaluation
    filtered.sort((a, b) => {
      const dateA = a.issuedAt || a.createdAt;
      const dateB = b.issuedAt || b.createdAt;
      const timeA = dateA?.toMillis?.() || new Date(dateA).getTime();
      const timeB = dateB?.toMillis?.() || new Date(dateB).getTime();
      return timeA - timeB; // Asc order for rule evaluation
    });

    // Additional filtering will be done in the service layer based on scope
    return filtered;
  }

  /**
   * Update mention status to actioned
   * @param {string} mentionId - Mention ID
   * @param {Object} actionDetails - Details of the action taken (MUST include schoolId)
   * @returns {Promise<Object>} Updated mention
   */
  async markAsActioned(mentionId, actionDetails = {}) {
    if (!actionDetails.schoolId) {
      throw new Error('schoolId is required in actionDetails for hierarchical mentions collection');
    }

    return this.update(mentionId, {
      schoolId: actionDetails.schoolId,
      actioned: true,
      actionedAt: Timestamp.now(),
      actionedBy: actionDetails.actionedBy || 'system',
      triggeredConsequences: actionDetails.triggeredConsequences || []
    });
  }

  /**
   * Batch update multiple mentions as actioned
   * @param {Array} mentionIds - Array of mention IDs
   * @param {Object} actionDetails - Details of the action taken
   * @returns {Promise<Array>} Array of updated mentions
   */
  async batchMarkAsActioned(mentionIds, actionDetails = {}) {
    const promises = mentionIds.map(id =>
      this.markAsActioned(id, actionDetails)
    );

    return Promise.all(promises);
  }

  /**
   * Get unactioned mentions count for a student
   * @param {string} studentId - Student ID
   * @param {Date} startDate - Optional start date
   * @returns {Promise<number>} Count of unactioned mentions
   */
  async getUnactionedCount(studentId, startDate = null) {
    const constraints = [
      where('studentId', '==', studentId),
      where('actioned', '==', false)
    ];

    if (startDate) {
      constraints.push(where('issuedAt', '>=', Timestamp.fromDate(startDate)));
    }

    return this.count(constraints);
  }

  /**
   * Get mentions by offense category
   * @param {string} category - Offense category
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of mentions
   */
  async getByOffenseCategory(category, options = {}) {
    const constraints = [
      where('offenseCategory', '==', category)
    ];

    if (options.studentId) {
      constraints.push(where('studentId', '==', options.studentId));
    }

    if (options.actioned !== undefined) {
      constraints.push(where('actioned', '==', options.actioned));
    }

    const results = await this.queryDocuments(constraints);

    // Sort client-side by issuedAt (falling back to createdAt)
    results.sort((a, b) => {
      const dateA = a.issuedAt || a.createdAt;
      const dateB = b.issuedAt || b.createdAt;
      const timeA = dateA?.toMillis?.() || new Date(dateA).getTime();
      const timeB = dateB?.toMillis?.() || new Date(dateB).getTime();
      return timeB - timeA;
    });

    // Apply date filtering client-side
    let filtered = results;
    if (options.startDate) {
      const startTime = options.startDate.getTime();
      filtered = filtered.filter(m => {
        const date = m.issuedAt || m.createdAt;
        const time = date?.toMillis?.() || new Date(date).getTime();
        return time >= startTime;
      });
    }

    if (options.limit) {
      filtered = filtered.slice(0, options.limit);
    }

    return filtered;
  }

  /**
   * Create mention with optional transaction
   * @param {Object} mentionData - Mention data (must include schoolId for hierarchical collections)
   * @param {Object} transaction - Firestore transaction (optional)
   * @returns {Promise<Object>} Created mention
   */
  async createWithTransaction(mentionData, transaction = null) {
    // CRITICAL: Validate schoolId for hierarchical collection
    if (this.isHierarchical && !mentionData.schoolId) {
      throw new Error('schoolId is required in mentionData for hierarchical mentions collection');
    }

    if (transaction) {
      // Handle within transaction using proper hierarchical reference
      const collectionRef = this.getCollectionRef(mentionData.schoolId);
      const docRef = doc(collectionRef);
      const data = {
        ...mentionData,
        createdAt: mentionData.createdAt || Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      transaction.set(docRef, data);

      return {
        id: docRef.id,
        ...data
      };
    } else {
      // Use regular create
      return this.create(mentionData);
    }
  }

  /**
   * Get recent mentions for statistics
   * @param {number} days - Number of days to look back
   * @param {Object} filters - Optional filters
   * @returns {Promise<Array>} Array of recent mentions
   */
  async getRecentMentions(days = 7, filters = {}) {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const constraints = [];

    if (filters.classId) {
      constraints.push(where('classId', '==', filters.classId));
    }

    if (filters.studentId) {
      constraints.push(where('studentId', '==', filters.studentId));
    }

    const results = await this.queryDocuments(constraints);

    // Filter by date and sort client-side by issuedAt (falling back to createdAt)
    const startTime = startDate.getTime();
    const filtered = results.filter(m => {
      const date = m.issuedAt || m.createdAt;
      const time = date?.toMillis?.() || new Date(date).getTime();
      return time >= startTime;
    });

    filtered.sort((a, b) => {
      const dateA = a.issuedAt || a.createdAt;
      const dateB = b.issuedAt || b.createdAt;
      const timeA = dateA?.toMillis?.() || new Date(dateA).getTime();
      const timeB = dateB?.toMillis?.() || new Date(dateB).getTime();
      return timeB - timeA;
    });

    return filtered;
  }
}
